import { api, type RoleShareStray, type RoleShareTask, type RoleShareTaskState, requestFailure } from '@/api'

/**
 * Choosing which of a role's tasks the catalog lists, on the role's page and on its team's share. A ticked task is
 * published (or brought up to date) through its own version save, which lists it on the role once this company has the
 * role in the catalog; an unticked one is withdrawn, or kept private, and remembered as left out. Strays, entries
 * still on the role whose tasks no longer belong there, come off it. Every write is the same version save the task's
 * own page makes, so the catalog changes the way it always does, one task at a time.
 */

/** States a person decides on: the task has a version and is the company's own to publish or keep. */
const DECIDABLE: ReadonlySet<RoleShareTaskState> = new Set(['listed', 'outdated', 'elsewhere', 'unlisted', 'left_out'])

/** States ticked when a listing opens: in the catalog already, or not decided on yet. */
const TICKED: ReadonlySet<RoleShareTaskState> = new Set(['listed', 'outdated', 'elsewhere', 'unlisted'])

export function decidable(task: RoleShareTask): boolean {
    return task.currentVersionId !== null && DECIDABLE.has(task.state)
}

/** The tasks ticked when the listing opens, by id. */
export function initiallyTicked(tasks: readonly RoleShareTask[]): Set<string> {
    return new Set(tasks.filter(task => decidable(task) && TICKED.has(task.state)).map(task => task.id))
}

/** What saving the listing does to one task: publish it, withdraw it (or keep it private), or nothing. */
export function listingAction(task: RoleShareTask, ticked: boolean): 'public' | 'private' | null {
    if (!decidable(task)) return null
    if (ticked) return task.state === 'listed' ? null : 'public'
    return task.state === 'left_out' ? null : 'private'
}

/** What one row says it is now, and what saving would do to it. */
export function listingLabel(task: RoleShareTask, ticked: boolean, rolePublic: boolean): string {
    if (task.state === 'built_in') return 'Built in: every company has it already'
    if (task.state === 'deprecated') return 'Retired'
    if (task.state === 'no_version') return 'No version yet, stays behind'
    const action = listingAction(task, ticked)
    if (action === 'private') return task.state === 'unlisted' ? 'Stays private' : 'Will be withdrawn'
    if (action === 'public') {
        if (task.state === 'outdated') return `Catalog has v${task.catalogVersion}: will update to v${task.version}`
        if (task.state === 'elsewhere')
            return rolePublic ? 'In the catalog: will be listed on this role' : 'In the catalog'
        return task.state === 'left_out' ? 'Left out: will be published' : 'Will be published'
    }
    if (task.state === 'left_out') return 'Left out'
    return `In the catalog, v${task.catalogVersion ?? task.version}`
}

/** What happens to a stray when the listing is saved. */
export function strayLabel(stray: RoleShareStray): string {
    if (stray.reason === 'moved') return 'Now runs as another role: comes off this one'
    if (stray.reason === 'retired') return 'Retired: will be retired in the catalog too'
    return 'Deleted: will be retired in the catalog'
}

/** How many writes saving would make: the tasks whose tick changes something, and every stray. */
export function pendingWrites(
    tasks: readonly RoleShareTask[],
    ticked: ReadonlySet<string>,
    strays: readonly RoleShareStray[],
): number {
    return tasks.filter(task => listingAction(task, ticked.has(task.id)) !== null).length + strays.length
}

/** The server's sentence for a refusal, or a plain fallback: a code alone is nothing a person can act on. */
export function refusalSentence(caught: unknown, fallback: string): string {
    if (!(caught instanceof Error)) return fallback
    const failure = requestFailure(caught)
    return failure !== null && caught.message === String(failure.status) ? fallback : caught.message
}

/**
 * Saves the listing, one task at a time so a refusal names its task, and answers the refusals keyed by task id (or,
 * for a stray, by its entry id). A refusal stops nothing else: the rest of the listing is saved.
 */
export async function saveListing(
    tasks: readonly RoleShareTask[],
    ticked: ReadonlySet<string>,
    strays: readonly RoleShareStray[],
    progress: (sentence: string) => void,
): Promise<Record<string, string>> {
    const refused: Record<string, string> = {}
    for (const task of tasks) {
        const visibility = listingAction(task, ticked.has(task.id))
        if (!visibility || !task.currentVersionId) continue
        progress(visibility === 'public' ? `Publishing ${task.name}…` : `Withdrawing ${task.name}…`)
        try {
            await api.saveTaskVersion(task.id, { baseVersionId: task.currentVersionId, visibility })
        } catch (caught) {
            refused[task.id] = refusalSentence(caught, 'The catalog refused this task.')
        }
    }
    for (const stray of strays) {
        progress(`Taking ${stray.name} off the role…`)
        try {
            if (stray.reason === 'moved' && stray.sourceTaskId && stray.sourceVersionId) {
                // Published again, the entry follows its task to the role it runs as now, or onto none.
                await api.saveTaskVersion(stray.sourceTaskId, {
                    baseVersionId: stray.sourceVersionId,
                    visibility: 'public',
                })
            } else {
                await api.deprecateCatalogTask(stray.entryId)
            }
        } catch (caught) {
            refused[stray.entryId] = refusalSentence(caught, 'The catalog refused this change.')
        }
    }
    return refused
}
